import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { FKType, SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { User } from './User'

type DeviceConstructorArgs = ConstructorArgs<() => Device>

export class Device extends BaseRecord {
    readonly recordType = 'device'

    device?: string
    address?: string
    calendar?: string
    availability?: string
    hipaa?: boolean
    onlyMine?: boolean
    make?: string
    type?: string
    ttl?: number
    lastLoginDate?: moment.Moment
    lastSyncDate?: moment.Moment
    lastContactDate?: moment.Moment
    lastCalendarDate?: moment.Moment
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<DeviceConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.type = data.type ?? undefined
        this.make = data.make ?? undefined
        this.device = data.device ?? undefined
        this.address = data.address ?? undefined
        this.calendar = data.calendar ?? undefined
        this.availability = data.availability ?? undefined
        this.hipaa = data.hipaa ?? false
        this.onlyMine = data.only_mine ?? false
        this.ttl = this.propertyFromIntArg(data.ttl)
        this.lastSyncDate = this.propertyFromTimestampArg(data.last_sync_date)
        this.lastLoginDate = this.propertyFromTimestampArg(data.last_login_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastContactDate = this.propertyFromTimestampArg(data.last_contact_date)
        this.lastCalendarDate = this.propertyFromTimestampArg(data.last_calendar_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<DeviceConstructorArgs>): Device {
        return new Device({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Device {
        const device = CacheFactory.getInstance().getRecord(Device, id) ?? new Device({ id })
        return device
    }

    static async currentDevice(): Promise<Device | undefined> {
        if (!PSApplication.me().id) return undefined
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('devices')
        sql.filterBy('devices.owner=?', PSApplication.me().id!)
        sql.filterBy('devices.device=?', PSApplication.deviceId())
        const resultSet = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return !!resultSet ? new Device(JSON.parse(resultSet['jsonData'])) : undefined
    }

    get calendars(): string[] {
        if (this.availability) return this.availability.split(',')
        return []
    }

    set calendars(calendars: string[]) {
        this.availability = calendars && calendars.length > 0 ? calendars.join(',') : ''
    }

    toString(): string {
        let string = ''
        if (this.id) string += `id: ${this.id} `
        if (this.device) string += `device: ${this.device} `
        if (this.make) string += `make: ${this.make} `
        if (this.type) string += `type: ${this.type} `
        return string.trim()
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.lastLoginDate) this.lastLoginDate = moment(data.lastLoginDate)
        if (data.lastSyncDate) this.lastSyncDate = moment(data.lastSyncDate)
        if (data.lastContactDate) this.lastContactDate = moment(data.lastContactDate)
        if (data.lastCalendarDate) this.lastCalendarDate = moment(data.lastCalendarDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner'],
    }

    static dataType(): DataType {
        return 'devices'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['ttl'] = this.ttl ?? ''
        dict['calendar'] = this.calendar ?? ''
        dict['availability'] = this.availability ?? ''
        dict['hipaa'] = this.hipaa
        dict['only_mine'] = this.onlyMine
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
}
